/**
 * YourPantry – Pantry/inventory screen (v2 redesign).
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { authenticatedFetch } from '../utils/auth.js'
import { useToast } from '../hooks/useToast.js'
import { notifySavedListsInvalidated } from '../savedListsCache.js'
import { appendRecipeIngredientsToCurrentShoppingList } from '../utils/appendRecipeIngredientsToShoppingList.js'
import DismissibleOnboardingMessage from './DismissibleOnboardingMessage.jsx'
import { Button, Input, Modal } from '../design-system/primitives/index.js'
import StateBanner from './StateBanner/StateBanner.jsx'
import useOnboardingVisibility from '../hooks/useOnboardingVisibility.js'
import { ONBOARDING_BANNER_FLAGS, ONBOARDING_MESSAGES } from '../utils/onboardingRegistry.js'
import PantryQuickAddTabs from './pantry/PantryQuickAddTabs.jsx'
import PantryAddFeedbackBanner from './pantry/PantryAddFeedbackBanner.jsx'
import PantrySpellingSuggestions from './pantry/PantrySpellingSuggestions.jsx'
import PantryReviewConfirm from './pantry/PantryReviewConfirm.jsx'
import { hasReceiptSkippedItems } from './pantry/pantryAddFeedbackUtils.js'
import PantryLowStockBanner from './pantry/PantryLowStockBanner.jsx'
import PantryUnlockMealsBanner from './pantry/PantryUnlockMealsBanner.jsx'
import PantryRemoveUndoToast from './pantry/PantryRemoveUndoToast.jsx'
import PantrySkeleton from './pantry/PantrySkeleton.jsx'
import ExpiryBadge from './pantry/ExpiryBadge.jsx'
import {
  PANTRY_CATEGORIES,
  PANTRY_STARTER_SESSION_KEY,
  EXPIRING_BANNER_DAYS,
  LOW_STOCK_THRESHOLD,
  categoryToBucketKey,
  BUCKET_TO_CATEGORY,
  bucketKeysFromItems,
  formatPantryQuantity,
  getDaysLeft,
  computePeakPantryCount,
  getPantryContextEyebrow,
  getPantryPageEyebrow,
  isExpiringWithinDays,
  isFrozen,
  FREEZABLE_BUCKET_KEYS,
  isPantryShrinking,
  isPantryQuantityEditable,
  isRunningLowCountable,
  canonicalPantryQuantityUnit,
  formatPantryAddLine,
  normalizePantryListInput,
  notifyPantryInventoryChanged,
  pantryItemDisplayName,
  PANTRY_QUANTITY_UNIT_OPTIONS,
  parseJsonResponse,
  resolvePantryAddAmount,
  shouldSuppressExpiringPromo,
} from './pantry/pantryHelpers.js'
import './pantry/YourPantry.css'
import './planner/planner.css'

const HIGHLIGHT_CLEAR_MS = 4000
const EXPIRING_PROMO_SUPPRESS_MS = 60_000
const EXPIRING_BAND_DISMISS_SESSION_KEY = 'pantry_expiring_band_dismissed_session'
const BREAKPOINT_MOBILE = 768
const MOBILE_LIST_PREVIEW_COUNT = 10
const EMPTY_GROUPED = { proteins: [], fruits_vegetables: [], dairy: [], spices: [], staples: [] }

function useGroupedInventory(hasKitchenAccess) {
  const [grouped, setGrouped] = useState(EMPTY_GROUPED)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const hasLoadedRef = useRef(false)
  const fetchGrouped = useCallback(async () => {
    if (!hasKitchenAccess) {
      setGrouped(EMPTY_GROUPED)
      setLoading(false)
      hasLoadedRef.current = false
      return
    }
    if (!hasLoadedRef.current) setLoading(true)
    setError('')
    try {
      const res = await authenticatedFetch('/inventory/grouped')
      if (!res.ok) throw new Error(res.status === 401 ? 'Please log in' : 'Failed to load pantry')
      const data = await parseJsonResponse(res)
      setGrouped({
        proteins: data.proteins || [],
        fruits_vegetables: data.fruits_vegetables || [],
        dairy: data.dairy || [],
        spices: data.spices || [],
        staples: data.staples || [],
      })
      hasLoadedRef.current = true
    } catch (e) {
      setError(e.message || 'Could not load pantry')
      setGrouped(EMPTY_GROUPED)
    } finally {
      setLoading(false)
    }
  }, [hasKitchenAccess])

  useEffect(() => {
    fetchGrouped()
  }, [fetchGrouped])

  return { grouped, loading, error, refresh: fetchGrouped }
}

function QtyPencilIcon() {
  return (
    <svg className="pantry-item-row__qty-pencil" width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M8.5 1.5a1.5 1.5 0 0 1 2 2l-7 7-2.5.5.5-2.5 7-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function NamePencilIcon() {
  return (
    <svg className="pantry-item-row__name-pencil" width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M8.5 1.5a1.5 1.5 0 0 1 2 2l-7 7-2.5.5.5-2.5 7-7z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function QtyConfirmIcon() {
  return (
    <svg className="pantry-item-row__qty-confirm-icon" width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.5 6.5 11.5 12.5 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PantryUnitSelect({ value, onChange, onBlur, ariaLabel, className, disabled }) {
  const known = PANTRY_QUANTITY_UNIT_OPTIONS.some((opt) => opt.value === value)
  return (
    <select
      className={className}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {PANTRY_QUANTITY_UNIT_OPTIONS.map((opt) => (
        <option key={opt.value || 'count'} value={opt.value}>
          {opt.label}
        </option>
      ))}
      {value && !known ? <option value={value}>{value}</option> : null}
    </select>
  )
}

function PantryItemRow({
  item,
  recentlyAddedIds,
  editMode,
  selectedItemIds,
  onToggleItem,
  onQuantityChange,
  quantityBusy,
  isEditing = false,
  onStartEdit,
  onStopEdit,
  onRegisterConfirm,
  isEditingName = false,
  onStartNameEdit,
  onStopNameEdit,
  onDisplayNameChange,
  nameBusy = false,
  isMobile = false,
  hideQuantity = false,
}) {
  const idStr = typeof item.id === 'string' ? item.id : item.id != null ? String(item.id) : ''
  const isJustAdded = idStr && recentlyAddedIds.includes(idStr)
  const displayName = pantryItemDisplayName(item)
  const frozen = isFrozen(item)
  const daysLeft = getDaysLeft(item)
  const isSelected = editMode && idStr && selectedItemIds?.has(idStr)
  const low = isRunningLowCountable(item)
  const qty = item?.quantity
  const hasCount = qty != null && qty > 0
  const qtyLabel = formatPantryQuantity(item)
  const editable = isPantryQuantityEditable(item)
  const busy = Boolean(quantityBusy)
  const renamingBusy = Boolean(nameBusy)
  const [draft, setDraft] = useState(String(qty ?? ''))
  const [unitDraft, setUnitDraft] = useState(() => canonicalPantryQuantityUnit(item?.quantity_unit))
  const unitDraftRef = useRef(canonicalPantryQuantityUnit(item?.quantity_unit))
  const [nameDraft, setNameDraft] = useState(displayName)
  const [nameExpanded, setNameExpanded] = useState(false)
  const [nameTruncated, setNameTruncated] = useState(false)
  const inputRef = useRef(null)
  const nameRef = useRef(null)

  useEffect(() => {
    if (!isEditing) return
    setDraft(hasCount ? String(qty) : '1')
    const nextUnit = canonicalPantryQuantityUnit(item?.quantity_unit)
    unitDraftRef.current = nextUnit
    setUnitDraft(nextUnit)
  }, [isEditing, hasCount, qty, item?.quantity_unit])

  useEffect(() => {
    if (!isEditing || !inputRef.current) return
    inputRef.current.focus()
    inputRef.current.select()
  }, [isEditing])

  useEffect(() => {
    if (!isEditingName) return
    setNameDraft(displayName)
  }, [isEditingName, displayName])

  useEffect(() => {
    setNameExpanded(false)
  }, [displayName, idStr])

  useEffect(() => {
    const el = nameRef.current
    if (!el || nameExpanded || isEditingName) {
      setNameTruncated(false)
      return undefined
    }
    const measure = () => {
      setNameTruncated(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [displayName, nameExpanded, isMobile, low, isEditing, isEditingName])

  const confirmEdit = useCallback(() => {
    if (!isEditing) return
    const parsed = parseInt(draft, 10)
    if (!Number.isNaN(parsed) && parsed >= 0) {
      if (parsed === 0) {
        onQuantityChange?.(item, 0)
      } else {
        const nextUnit = canonicalPantryQuantityUnit(unitDraftRef.current) || null
        const currentQty = hasCount ? qty : null
        const currentUnit = canonicalPantryQuantityUnit(item?.quantity_unit) || null
        if (parsed !== currentQty || nextUnit !== currentUnit) {
          onQuantityChange?.(item, parsed, nextUnit)
        }
      }
    }
    onStopEdit?.()
  }, [draft, hasCount, isEditing, item, onQuantityChange, onStopEdit, qty])

  const cancelEdit = useCallback(() => {
    setDraft(hasCount ? String(qty) : '')
    onStopEdit?.()
  }, [hasCount, onStopEdit, qty])

  const confirmNameEdit = useCallback(() => {
    if (!isEditingName) return
    const next = nameDraft.trim()
    if (!next) {
      setNameDraft(displayName)
      onStopNameEdit?.()
      return
    }
    if (next !== displayName) onDisplayNameChange?.(item, next)
    onStopNameEdit?.()
  }, [displayName, isEditingName, item, nameDraft, onDisplayNameChange, onStopNameEdit])

  const cancelNameEdit = useCallback(() => {
    setNameDraft(displayName)
    onStopNameEdit?.()
  }, [displayName, onStopNameEdit])

  useEffect(() => {
    if (!isEditing) return undefined
    onRegisterConfirm?.(confirmEdit)
    return () => onRegisterConfirm?.(null)
  }, [confirmEdit, isEditing, onRegisterConfirm])

  const stepDraft = (delta) => {
    const base = parseInt(draft, 10)
    const current = Number.isNaN(base) ? (qty ?? 0) : base
    const next = Math.max(0, current + delta)
    setDraft(String(next))
    const priorQty = qty != null && qty > 0 ? qty : 0
    if (next === 0 && priorQty > 0) {
      onQuantityChange?.(item, 0)
      onStopEdit?.()
    }
  }

  const rowClass = [
    'pantry-item-row',
    editMode ? 'pantry-item-row--selectable' : '',
    isSelected ? 'pantry-item-row--selected' : '',
    isJustAdded ? 'pantry-item-row--just-added' : '',
    isEditing ? 'pantry-item-row--editing' : '',
    isEditingName ? 'pantry-item-row--renaming' : '',
    isMobile ? 'pantry-item-row--mobile' : '',
    daysLeft != null && daysLeft <= 2 ? 'pantry-item-row--urgent' : '',
    daysLeft != null && daysLeft > 2 ? 'pantry-item-row--soon' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const preventPointerBlur = (e) => e.preventDefault()
  const canToggleName = !editMode && !isEditingName && (nameTruncated || nameExpanded)
  const canRename = !editMode && !isMobile && Boolean(onStartNameEdit)
  const toggleNameExpanded = (e) => {
    e.stopPropagation()
    setNameExpanded((prev) => !prev)
  }

  return (
    <div
      className={rowClass}
      onClick={editMode ? () => idStr && onToggleItem?.(idStr) : undefined}
      role={editMode ? 'button' : undefined}
      tabIndex={editMode ? 0 : undefined}
      onKeyDown={
        editMode
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                if (idStr) onToggleItem?.(idStr)
              }
            }
          : undefined
      }
    >
      {editMode ? (
        <span
          className={`pantry-item-row__checkbox${isSelected ? ' pantry-item-row__checkbox--on' : ''}`}
          aria-hidden="true"
        >
          {isSelected ? '✓' : ''}
        </span>
      ) : null}
      {isEditingName ? (
        <div className="pantry-item-row__name-edit-form" onClick={(e) => e.stopPropagation()}>
          <input
            autoFocus
            type="text"
            maxLength={255}
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                confirmNameEdit()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                cancelNameEdit()
              }
            }}
            onBlur={confirmNameEdit}
            disabled={renamingBusy}
            aria-label={`Rename ${displayName}`}
            className="input pantry-item-row__name-input"
          />
        </div>
      ) : (
        <span className="pantry-item-row__name-wrap">
          <span
            ref={nameRef}
            className={[
              'pantry-item-row__name',
              daysLeft != null ? 'pantry-item-row__name--expiring' : '',
              nameExpanded ? 'pantry-item-row__name--expanded' : '',
              canToggleName ? 'pantry-item-row__name--interactive' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            title={displayName}
            onClick={canToggleName ? toggleNameExpanded : undefined}
            onKeyDown={
              canToggleName
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleNameExpanded(e)
                    }
                  }
                : undefined
            }
            role={canToggleName ? 'button' : undefined}
            tabIndex={canToggleName ? 0 : undefined}
            aria-expanded={canToggleName ? nameExpanded : undefined}
            aria-label={
              canToggleName
                ? nameExpanded
                  ? `${displayName}${low ? ', running low' : ''}. Collapse name`
                  : `${displayName}${low ? ', running low' : ''}. Show full name`
                : undefined
            }
          >
            {displayName}
            {low ? <span className="pantry-item-row__low">↓ low</span> : null}
          </span>
          {canRename ? (
            <button
              type="button"
              className="pantry-item-row__name-edit"
              onClick={(e) => {
                e.stopPropagation()
                if (idStr) onStartNameEdit?.(idStr)
              }}
              disabled={renamingBusy}
              title="Rename"
              aria-label={`Rename ${displayName}`}
            >
              <NamePencilIcon />
            </button>
          ) : null}
        </span>
      )}
      {isJustAdded ? <span className="pantry-item-row__added-tag">✓ Added</span> : null}
      {!isEditing ? (
        <div className="pantry-item-row__badge-slot">
          {frozen ? <span className="pantry-frozen-badge">Frozen</span> : <ExpiryBadge daysLeft={daysLeft} />}
        </div>
      ) : null}
      {!hideQuantity && !editMode && isEditing && editable ? (
        <div className="pantry-item-row__qty-edit" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="pantry-item-row__qty-step"
            onMouseDown={preventPointerBlur}
            onPointerDown={preventPointerBlur}
            onClick={() => stepDraft(-1)}
            disabled={busy || Number(draft) <= 0}
            aria-label={`Decrease amount for ${displayName}`}
          >
            −
          </button>
          <input
            ref={inputRef}
            type="number"
            min={0}
            inputMode="numeric"
            className="input pantry-item-row__qty-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                confirmEdit()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                cancelEdit()
              }
            }}
            onBlur={(e) => {
              if (isMobile) return
              const next = e.relatedTarget
              if (next && e.currentTarget.closest('.pantry-item-row__qty-edit')?.contains(next)) return
              confirmEdit()
            }}
            disabled={busy}
            aria-label={`Amount for ${displayName}`}
          />
          <button
            type="button"
            className="pantry-item-row__qty-step"
            onMouseDown={preventPointerBlur}
            onPointerDown={preventPointerBlur}
            onClick={() => stepDraft(1)}
            disabled={busy}
            aria-label={`Increase amount for ${displayName}`}
          >
            +
          </button>
          <PantryUnitSelect
            className="pantry-item-row__qty-unit"
            value={unitDraft}
            ariaLabel={`Unit for ${displayName}`}
            disabled={busy}
            onChange={(e) => {
              unitDraftRef.current = e.target.value
              setUnitDraft(e.target.value)
            }}
            onBlur={(e) => {
              if (isMobile) return
              const next = e.relatedTarget
              if (next && e.currentTarget.closest('.pantry-item-row__qty-edit')?.contains(next)) return
              confirmEdit()
            }}
          />
          <button
            type="button"
            className="pantry-item-row__qty-done"
            onMouseDown={preventPointerBlur}
            onPointerDown={preventPointerBlur}
            onClick={confirmEdit}
            disabled={busy}
            aria-label={`Save amount for ${displayName}`}
            title="Save amount"
          >
            {isMobile ? <QtyConfirmIcon /> : 'Done'}
          </button>
        </div>
      ) : !hideQuantity && !editMode && editable ? (
        <div className="pantry-item-row__qty-controls" onClick={(e) => e.stopPropagation()}>
          {hasCount ? (
            <button
              type="button"
              className="pantry-item-row__qty-badge"
              onClick={() => idStr && onStartEdit?.(idStr)}
              disabled={busy}
              title={isMobile ? 'Swipe left to adjust, or tap to edit' : 'Tap to update the amount'}
              aria-label={
                isMobile
                  ? `Amount for ${displayName}, currently ${qtyLabel}. Swipe left to adjust, or tap to set the unit.`
                  : `Update amount for ${displayName}, currently ${qtyLabel}`
              }
            >
              {qtyLabel}
              {!isMobile ? <QtyPencilIcon /> : null}
            </button>
          ) : (
            <button
              type="button"
              className="pantry-item-row__qty-add"
              onClick={() => idStr && onStartEdit?.(idStr)}
              disabled={busy}
              aria-label={`Add count for ${displayName}`}
            >
              + count
            </button>
          )}
        </div>
      ) : !hideQuantity && qtyLabel ? (
        <span className={`pantry-item-row__qty${editable ? '' : ' pantry-item-row__qty--fixed'}`}>{qtyLabel}</span>
      ) : null}
    </div>
  )
}

const SWIPE_STEPPER_WIDTH = 212
const SWIPE_REMOVE_WIDTH = 80

function SwipeableItemRow(props) {
  const { item, onSwipeRemove, onQuantityChange, quantityBusy, isEditing, onStartEdit, onStopEdit } = props
  const idStr = typeof item.id === 'string' ? item.id : item.id != null ? String(item.id) : ''
  const editable = isPantryQuantityEditable(item)
  const actionWidth = editable ? SWIPE_STEPPER_WIDTH : SWIPE_REMOVE_WIDTH
  const openThreshold = Math.round(actionWidth * 0.4)
  const qty = item?.quantity
  const hasCount = qty != null && qty > 0
  const busy = Boolean(quantityBusy)
  const displayName = pantryItemDisplayName(item)

  const [offset, setOffset] = useState(0)
  const [draft, setDraft] = useState(hasCount ? String(qty) : '1')
  const [stepperActive, setStepperActive] = useState(false)
  const offsetRef = useRef(0)
  const startX = useRef(0)
  const startY = useRef(0)
  const axisLock = useRef(null)
  const open = offset <= -openThreshold
  const inputRef = useRef(null)

  const setSwipeOffset = (next) => {
    offsetRef.current = next
    setOffset(next)
  }

  useEffect(() => {
    if (isEditing) {
      setSwipeOffset(0)
      setStepperActive(false)
    }
  }, [isEditing])

  useEffect(() => {
    if (!stepperActive || !editable) return
    setDraft(hasCount ? String(qty) : '1')
  }, [stepperActive, editable, hasCount, qty])

  useEffect(() => {
    if (!stepperActive || !editable || !inputRef.current) return
    inputRef.current.focus()
    inputRef.current.select()
  }, [stepperActive, editable])

  const closeSwipe = useCallback(() => {
    setSwipeOffset(0)
    setStepperActive(false)
  }, [])

  const confirmSwipeEdit = useCallback(() => {
    const parsed = parseInt(draft, 10)
    if (!Number.isNaN(parsed) && parsed >= 0) {
      const current = hasCount ? qty : null
      if (parsed !== current) onQuantityChange?.(item, parsed)
    }
    closeSwipe()
  }, [closeSwipe, draft, hasCount, item, onQuantityChange, qty])

  const stepDraft = (delta) => {
    const base = parseInt(draft, 10)
    const current = Number.isNaN(base) ? (qty ?? 0) : base
    const next = Math.max(0, current + delta)
    setDraft(String(next))
    const priorQty = qty != null && qty > 0 ? qty : 0
    if (next === 0 && priorQty > 0) {
      onQuantityChange?.(item, 0)
      closeSwipe()
    }
  }

  const onTouchStart = (e) => {
    if (isEditing) return
    startX.current = e.touches[0].clientX
    startY.current = e.touches[0].clientY
    axisLock.current = null
  }
  const onTouchMove = (e) => {
    if (isEditing) return
    const dx = e.touches[0].clientX - startX.current
    const dy = e.touches[0].clientY - startY.current
    if (axisLock.current == null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return
      axisLock.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
    }
    if (axisLock.current !== 'x') return
    if (dx < 0) setSwipeOffset(Math.max(-actionWidth, dx))
    else setSwipeOffset(0)
  }
  const onTouchEnd = () => {
    if (isEditing) {
      axisLock.current = null
      return
    }
    if (offsetRef.current <= -openThreshold) {
      setSwipeOffset(-actionWidth)
      if (editable) {
        setStepperActive(true)
        if (idStr) onStopEdit?.()
      }
    } else {
      setSwipeOffset(0)
      setStepperActive(false)
    }
    axisLock.current = null
  }

  const preventPointerBlur = (e) => e.preventDefault()

  return (
    <div className={`pantry-swipe-row${stepperActive ? ' pantry-swipe-row--editing' : ''}`}>
      {editable ? (
        <div
          className="pantry-swipe-row__action pantry-swipe-row__action--stepper"
          style={{ width: actionWidth }}
          role="group"
          aria-label={`Adjust count for ${displayName}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="pantry-item-row__qty-edit pantry-swipe-row__qty-edit">
            <button
              type="button"
              className="pantry-item-row__qty-step"
              onMouseDown={preventPointerBlur}
              onPointerDown={preventPointerBlur}
              onClick={() => stepDraft(-1)}
              disabled={busy || Number(draft) <= 0}
              aria-label={`Decrease count for ${displayName}`}
            >
              −
            </button>
            <input
              ref={inputRef}
              type="number"
              min={0}
              inputMode="numeric"
              className="input pantry-item-row__qty-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  confirmSwipeEdit()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  closeSwipe()
                }
              }}
              disabled={busy}
              aria-label={`Count for ${displayName}`}
            />
            <button
              type="button"
              className="pantry-item-row__qty-step"
              onMouseDown={preventPointerBlur}
              onPointerDown={preventPointerBlur}
              onClick={() => stepDraft(1)}
              disabled={busy}
              aria-label={`Increase count for ${displayName}`}
            >
              +
            </button>
            <button
              type="button"
              className="pantry-item-row__qty-done pantry-item-row__qty-done--icon"
              onMouseDown={preventPointerBlur}
              onPointerDown={preventPointerBlur}
              onClick={confirmSwipeEdit}
              disabled={busy}
              aria-label={`Save count for ${displayName}`}
              title="Save count"
            >
              <QtyConfirmIcon />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="pantry-swipe-row__action"
          style={{ width: actionWidth }}
          onClick={() => idStr && onSwipeRemove?.(idStr)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (idStr) onSwipeRemove?.(idStr)
            }
          }}
        >
          Remove
        </div>
      )}
      <div
        className={`pantry-swipe-row__content${open ? ' pantry-swipe-row__content--open' : ''}`}
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <PantryItemRow
          {...props}
          hideQuantity={editable && offset < 0}
          onStartEdit={
            editable
              ? (itemId) => {
                  closeSwipe()
                  onStartEdit?.(itemId)
                }
              : onStartEdit
          }
        />
      </div>
    </div>
  )
}

function CategoryInlineAdd({
  bucketKey,
  label,
  compactLabel = false,
  value,
  onChange,
  onSubmit,
  loading,
}) {
  const [open, setOpen] = useState(false)
  const triggerLabel = compactLabel ? 'Add to this section' : `Add to ${label}`

  const close = useCallback(() => {
    setOpen(false)
    onChange?.(bucketKey, '')
  }, [bucketKey, onChange])

  const submit = useCallback(async () => {
    if (!value.trim() || loading) return
    const ok = await onSubmit?.(bucketKey)
    if (ok) setOpen(false)
  }, [bucketKey, loading, onSubmit, value])

  if (!open) {
    return (
      <div className="pantry-category-add">
        <button
          type="button"
          className="pantry-category-add__trigger"
          onClick={() => setOpen(true)}
          disabled={loading}
        >
          + {triggerLabel}
        </button>
      </div>
    )
  }

  return (
    <div className="pantry-category-add pantry-category-add--open">
      <Input
        type="text"
        name={`pantry-add-${bucketKey}`}
        inputMode="text"
        autoComplete="off"
        autoFocus
        value={value}
        onChange={(e) => onChange?.(bucketKey, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            close()
          }
        }}
        placeholder={`Add to ${label}…`}
        aria-label={`Add item to ${label}`}
        className="pantry-category-add__input"
        disabled={loading}
      />
      <Button
        type="button"
        variant="primary"
        className="pantry-category-add__submit"
        onClick={submit}
        disabled={!value.trim() || loading}
      >
        {loading ? '…' : 'Add'}
      </Button>
      <button
        type="button"
        className="pantry-category-add__cancel"
        onClick={close}
        disabled={loading}
        aria-label="Cancel"
      >
        ×
      </button>
    </div>
  )
}

/** Shared ⋮ menu: Select items… (desktop accordion + mobile category list). */
function CategorySelectOverflow({
  label,
  disabled = false,
  onSelectItems,
  onOpenChange,
  forceClosed = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const overflowRef = useRef(null)

  useEffect(() => {
    onOpenChange?.(menuOpen)
  }, [menuOpen, onOpenChange])

  useEffect(() => {
    if (forceClosed) setMenuOpen(false)
  }, [forceClosed])

  useEffect(() => {
    if (!menuOpen) return undefined
    const onDoc = (e) => {
      if (!overflowRef.current?.contains(e.target)) setMenuOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  return (
    <div className="pantry-accordion__overflow" ref={overflowRef}>
      <button
        type="button"
        className={`pantry-accordion__overflow-btn${menuOpen ? ' open' : ''}`}
        title="Select items to remove, freeze, or thaw"
        aria-label={`More options for ${label}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(e) => {
          e.stopPropagation()
          setMenuOpen((open) => !open)
        }}
      >
        <span className="pantry-accordion__overflow-glyph" aria-hidden="true">
          ⋮
        </span>
      </button>
      {menuOpen ? (
        <div className="overflow-menu open" role="menu">
          <button
            type="button"
            role="menuitem"
            className="overflow-menu-item"
            disabled={disabled}
            title={disabled ? 'Finish reviewing before selecting items' : undefined}
            onClick={() => {
              if (disabled) return
              setMenuOpen(false)
              onSelectItems?.()
            }}
          >
            Select items…
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Compact mobile list header with ⋮ Select items… (active category only). */
function MobileCategoryListHeader({
  label,
  disabled = false,
  onSelectItems,
  onMenuOpenChange,
  forceClosed = false,
}) {
  return (
    <div className="pantry-v2__mobile-list-header">
      <span className="pantry-v2__mobile-list-header-label">{label}</span>
      <CategorySelectOverflow
        label={label}
        disabled={disabled}
        onSelectItems={onSelectItems}
        onOpenChange={onMenuOpenChange}
        forceClosed={forceClosed}
      />
    </div>
  )
}

/** One mobile category tab panel: optional ⋮ header + item list. */
function MobileCategoryPanel({
  label,
  bucketKey,
  hidden,
  listRef,
  items,
  editMode,
  selectedItemIds,
  onToggleItem,
  recentlyAddedIds,
  receiptReviewing,
  onEnterEditMode,
  onSwipeRemove,
  quantityBusyIds,
  editingItemId,
  onStartEdit,
  onStopEdit,
  onRegisterConfirm,
  editingNameItemId,
  onStartNameEdit,
  onStopNameEdit,
  onDisplayNameChange,
  nameBusyIds,
  onQuantityChange,
  inlineAddValue,
  onInlineAddChange,
  onInlineAdd,
  inlineAddLoading,
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const itemCount = items?.length || 0
  const showSelectMenu = !editMode && itemCount > 0

  useEffect(() => {
    if (editMode || hidden) setMenuOpen(false)
  }, [editMode, hidden])

  return (
    <div hidden={hidden} ref={listRef}>
      <div
        className={`pantry-accordion${menuOpen ? ' pantry-accordion--menu-open' : ''}`}
      >
        {showSelectMenu ? (
          <MobileCategoryListHeader
            label={label}
            disabled={receiptReviewing}
            onSelectItems={() => onEnterEditMode?.(bucketKey)}
            onMenuOpenChange={setMenuOpen}
            forceClosed={hidden || editMode}
          />
        ) : null}
        <div className="pantry-accordion__body">
          {itemCount === 0 ? (
            <p className="pantry-bucket-empty">No items yet</p>
          ) : (
            items.map((it) => {
              const itemId = String(it.id)
              const qtyEditProps = {
                onQuantityChange,
                quantityBusy: quantityBusyIds?.has(itemId),
                isEditing: editingItemId === itemId,
                onStartEdit,
                onStopEdit,
                onRegisterConfirm,
                isEditingName: editingNameItemId === itemId,
                onStartNameEdit,
                onStopNameEdit,
                onDisplayNameChange,
                nameBusy: nameBusyIds?.has(itemId),
                isMobile: true,
              }
              return editMode ? (
                <PantryItemRow
                  key={it.id}
                  item={it}
                  recentlyAddedIds={recentlyAddedIds}
                  editMode={editMode}
                  selectedItemIds={selectedItemIds}
                  onToggleItem={onToggleItem}
                />
              ) : (
                <SwipeableItemRow
                  key={it.id}
                  item={it}
                  recentlyAddedIds={recentlyAddedIds}
                  editMode={false}
                  selectedItemIds={selectedItemIds}
                  onToggleItem={onToggleItem}
                  onSwipeRemove={onSwipeRemove}
                  {...qtyEditProps}
                />
              )
            })
          )}
          {!editMode ? (
            <CategoryInlineAdd
              bucketKey={bucketKey}
              label={label}
              compactLabel
              value={inlineAddValue || ''}
              onChange={onInlineAddChange}
              onSubmit={onInlineAdd}
              loading={inlineAddLoading}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function CategoryAccordion({
  label,
  icon,
  bucketKey,
  items,
  expanded,
  onToggle,
  editMode,
  selectedItemIds,
  onToggleItem,
  recentlyAddedIds,
  isMobile,
  maxVisible,
  listExpanded,
  onExpandList,
  onSwipeRemove,
  onQuantityChange,
  quantityBusyIds,
  editingItemId,
  onStartEdit,
  onStopEdit,
  onRegisterConfirm,
  editingNameItemId,
  onStartNameEdit,
  onStopNameEdit,
  onDisplayNameChange,
  nameBusyIds,
  inlineAddValue,
  onInlineAddChange,
  onInlineAdd,
  inlineAddLoading,
  compactInlineLabel = false,
  showOverflowMenu = false,
  onSelectItems,
  selectItemsDisabled = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const itemCount = items?.length || 0
  const hasExpiring = (items || []).some((i) => isExpiringWithinDays(i, EXPIRING_BANNER_DAYS))
  const hasUrgent = (items || []).some((i) => {
    const d = getDaysLeft(i)
    return d != null && d <= 2
  })
  const showLimit = isMobile && maxVisible != null && !listExpanded && itemCount > maxVisible
  const displayItems = showLimit ? (items || []).slice(0, maxVisible) : items || []
  const moreCount = showLimit ? itemCount - maxVisible : 0
  const showOverflow = showOverflowMenu && itemCount > 0 && !editMode

  return (
    <div
      className={`pantry-accordion${itemCount === 0 ? ' pantry-accordion--empty' : ''}${
        menuOpen ? ' pantry-accordion--menu-open' : ''
      }`}
    >
      <div className={`pantry-accordion__header${showOverflow ? ' pantry-accordion__header--with-overflow' : ''}`}>
        <button type="button" className="pantry-accordion__trigger" onClick={onToggle} aria-expanded={expanded}>
          <span className="pantry-accordion__icon">{icon}</span>
          <span className="pantry-accordion__label">{label}</span>
          {hasExpiring ? (
            <span
              className={`pantry-accordion__expiry-dot${hasUrgent ? ' pantry-accordion__expiry-dot--urgent' : ' pantry-accordion__expiry-dot--soon'}`}
              aria-hidden="true"
            />
          ) : null}
          <span className="pantry-accordion__count">{itemCount === 0 ? 'empty' : itemCount}</span>
          <span className="pantry-accordion__chevron" aria-hidden="true">
            {expanded ? '▲' : '▼'}
          </span>
        </button>
        {showOverflow ? (
          <CategorySelectOverflow
            label={label}
            disabled={selectItemsDisabled}
            onSelectItems={() => onSelectItems?.(bucketKey)}
            onOpenChange={setMenuOpen}
          />
        ) : null}
      </div>
      {expanded ? (
        <div className="pantry-accordion__body">
          {itemCount === 0 ? <p className="pantry-bucket-empty">No items yet</p> : null}
          {displayItems.map((it) => {
            const itemId = String(it.id)
            const qtyEditProps = {
              onQuantityChange,
              quantityBusy: quantityBusyIds?.has(itemId),
              isEditing: editingItemId === itemId,
              onStartEdit,
              onStopEdit,
              onRegisterConfirm,
              isEditingName: editingNameItemId === itemId,
              onStartNameEdit,
              onStopNameEdit,
              onDisplayNameChange,
              nameBusy: nameBusyIds?.has(itemId),
              isMobile: Boolean(isMobile),
            }
            return isMobile && !editMode ? (
              <SwipeableItemRow
                key={it.id}
                item={it}
                recentlyAddedIds={recentlyAddedIds}
                editMode={editMode}
                selectedItemIds={selectedItemIds}
                onToggleItem={onToggleItem}
                onSwipeRemove={onSwipeRemove}
                {...qtyEditProps}
              />
            ) : (
              <PantryItemRow
                key={it.id}
                item={it}
                recentlyAddedIds={recentlyAddedIds}
                editMode={editMode}
                selectedItemIds={selectedItemIds}
                onToggleItem={onToggleItem}
                {...qtyEditProps}
              />
            )
          })}
          {moreCount > 0 ? (
            <button type="button" className="ghost pantry-bucket-show-more" onClick={onExpandList}>
              Show more ({moreCount} more)
            </button>
          ) : null}
          {!editMode ? (
            <CategoryInlineAdd
              bucketKey={bucketKey}
              label={label}
              compactLabel={compactInlineLabel}
              value={inlineAddValue || ''}
              onChange={onInlineAddChange}
              onSubmit={onInlineAdd}
              loading={inlineAddLoading}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function ExpiringTile({ item, compact }) {
  const name = pantryItemDisplayName(item)
  const daysLeft = getDaysLeft(item)
  return (
    <div className={compact ? 'pantry-expiring-band__tile' : 'pantry-use-it-sidebar__tile'}>
      <div className={compact ? 'pantry-expiring-band__tile-name' : 'pantry-use-it-sidebar__tile-name'}>{name}</div>
      <ExpiryBadge daysLeft={daysLeft} size="sm" />
    </div>
  )
}

function formatAddedList(names) {
  const clean = names.map((name) => String(name || '').trim()).filter(Boolean)
  if (clean.length <= 1) return clean[0] || ''
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`
  if (clean.length === 3) return `${clean[0]}, ${clean[1]}, and ${clean[2]}`
  return `${clean.slice(0, 3).join(', ')}, and ${clean.length - 3} more`
}

export default function YourPantry({
  onAskAI,
  showGoShoppingBanner = false,
  onGoShopping,
  openReceiptTab = false,
  onReceiptTabOpened,
  guestMode = false,
  onGuestOnboardingContinue,
  onInventorySettled,
  footer = null,
} = {}) {
  const { isAuthenticated: accountAuthenticated } = useAuth()
  const isAuthenticated = accountAuthenticated || guestMode
  const { showToast } = useToast()
  const { isVisible: isPantryIntroVisible } = useOnboardingVisibility(ONBOARDING_MESSAGES.pantryIntro.storageKey)
  const { grouped, loading, error, refresh } = useGroupedInventory(isAuthenticated)

  useEffect(() => {
    if (loading) return
    onInventorySettled?.()
  }, [loading, onInventorySettled])

  const [starterSessionDismissed, setStarterSessionDismissed] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PANTRY_STARTER_SESSION_KEY) === '1'
    } catch {
      return false
    }
  })
  const [expiringBandDismissed, setExpiringBandDismissed] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXPIRING_BAND_DISMISS_SESSION_KEY) === '1'
    } catch {
      return false
    }
  })
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= BREAKPOINT_MOBILE)
  const [receiptText, setReceiptText] = useState('')
  const [receiptFromPdf, setReceiptFromPdf] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [receiptBusyKind, setReceiptBusyKind] = useState(null)
  const [receiptReviewing, setReceiptReviewing] = useState(false)
  const [receiptCandidates, setReceiptCandidates] = useState([])
  const [receiptUnrecognized, setReceiptUnrecognized] = useState([])
  const [receiptCommitError, setReceiptCommitError] = useState(null)
  const [receiptCommitLoading, setReceiptCommitLoading] = useState(false)
  const [receiptInputNotice, setReceiptInputNotice] = useState(null)
  const [lastResult, setLastResult] = useState(null)
  const [lastAddedIds, setLastAddedIds] = useState([])
  const [lastAddedNames, setLastAddedNames] = useState([])
  const [starterAddedNames, setStarterAddedNames] = useState([])
  const [editMode, setEditMode] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set())
  const [bulkRemoveConfirm, setBulkRemoveConfirm] = useState(false)
  const [bulkRemoving, setBulkRemoving] = useState(false)
  const [manualItemName, setManualItemName] = useState('')
  const [manualAddQuantity, setManualAddQuantity] = useState('')
  const [manualAddUnit, setManualAddUnit] = useState('')
  const [manualAddLoading, setManualAddLoading] = useState(false)
  const [quantityBusyIds, setQuantityBusyIds] = useState(() => new Set())
  const [bulkFreezing, setBulkFreezing] = useState(false)
  const [editingItemId, setEditingItemId] = useState(null)
  const [editingNameItemId, setEditingNameItemId] = useState(null)
  const [nameBusyIds, setNameBusyIds] = useState(() => new Set())
  const confirmQtyEditRef = useRef(null)
  const [inlineAddByBucket, setInlineAddByBucket] = useState(() =>
    Object.fromEntries(PANTRY_CATEGORIES.map(({ key }) => [key, ''])),
  )
  const [inlineAddLoadingBucket, setInlineAddLoadingBucket] = useState(null)
  const [pantryActiveTab, setPantryActiveTab] = useState('fruits_vegetables')
  const [accordionExpanded, setAccordionExpanded] = useState(() =>
    Object.fromEntries(PANTRY_CATEGORIES.map(({ key }) => [key, false])),
  )
  const [addMethodTab, setAddMethodTab] = useState('item')
  const [nowMs, setNowMs] = useState(Date.now())
  const [undoToast, setUndoToast] = useState(null)
  const [addingToShopIds, setAddingToShopIds] = useState(() => new Set())
  const [suppressExpiringPromoUntil, setSuppressExpiringPromoUntil] = useState(0)
  const peakPantryCountRef = useRef(0)
  const listSectionRef = useRef(null)
  const inventoryToolbarRef = useRef(null)
  const reviewConfirmRef = useRef(null)
  const receiptPdfInputRef = useRef(null)
  const addingToShopIdsRef = useRef(new Set())

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= BREAKPOINT_MOBILE)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (lastAddedIds.length === 0) return
    const t = setTimeout(() => setLastAddedIds([]), HIGHLIGHT_CLEAR_MS)
    return () => clearTimeout(t)
  }, [lastAddedIds])

  // Success-only feedback has no close control; clear it after a few seconds.
  // Partial (unrecognized) and error banners stay until dismissed.
  useEffect(() => {
    if (!lastResult || lastResult.error || lastResult.notice) return
    const hasSuccess =
      (lastResult.added ?? 0) > 0 ||
      (lastResult.updated ?? 0) > 0 ||
      (lastResult.removed ?? 0) > 0
    if (!hasSuccess) return
    const hasPartial = hasReceiptSkippedItems(lastResult.not_imported)
    if (hasPartial) return
    const t = setTimeout(() => setLastResult(null), HIGHLIGHT_CLEAR_MS)
    return () => clearTimeout(t)
  }, [lastResult])

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const scrollInventoryToolbarIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      inventoryToolbarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }, [])

  const openAddPanel = useCallback(
    (tab = 'item') => {
      setEditMode(false)
      setSelectedItemIds(new Set())
      setBulkRemoveConfirm(false)
      setAddMethodTab(tab)
      scrollInventoryToolbarIntoView()
    },
    [scrollInventoryToolbarIntoView],
  )

  const resetReceiptReview = useCallback(() => {
    setReceiptReviewing(false)
    setReceiptFromPdf(false)
    setReceiptCandidates([])
    setReceiptUnrecognized([])
    setReceiptCommitError(null)
    setReceiptCommitLoading(false)
  }, [])

  const handleReceiptBackToInput = useCallback(() => {
    resetReceiptReview()
    setReceiptInputNotice(null)
  }, [resetReceiptReview])

  const handleReceiptEmptyReturn = useCallback(() => {
    resetReceiptReview()
    setReceiptInputNotice({ notice: 'returned_empty' })
  }, [resetReceiptReview])

  useEffect(() => {
    if (!openReceiptTab) return
    openAddPanel('receipt')
    onReceiptTabOpened?.()
  }, [openReceiptTab, onReceiptTabOpened, openAddPanel])

  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape' && bulkRemoveConfirm && !bulkRemoving && !bulkFreezing) {
        setBulkRemoveConfirm(false)
      }
    }
    if (bulkRemoveConfirm) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [bulkRemoveConfirm, bulkRemoving, bulkFreezing])

  const totalItems =
    (grouped.proteins?.length || 0) +
    (grouped.fruits_vegetables?.length || 0) +
    (grouped.dairy?.length || 0) +
    (grouped.spices?.length || 0) +
    (grouped.staples?.length || 0)

  const allPantryItems = useMemo(
    () => PANTRY_CATEGORIES.flatMap(({ key }) => grouped[key] || []),
    [grouped],
  )

  const itemById = useMemo(() => {
    const m = new Map()
    for (const it of allPantryItems) {
      if (it?.id != null) m.set(String(it.id), it)
    }
    return m
  }, [allPantryItems])

  const expiringItems = useMemo(() => {
    return allPantryItems
      .filter((item) => isExpiringWithinDays(item, EXPIRING_BANNER_DAYS))
      .sort((a, b) => (getDaysLeft(a) ?? 99) - (getDaysLeft(b) ?? 99))
  }, [allPantryItems])

  const runningLowItems = useMemo(
    () => allPantryItems.filter(isRunningLowCountable),
    [allPantryItems],
  )

  const selectedCount = selectedItemIds.size
  const freezableIdSet = useMemo(() => {
    const ids = new Set()
    for (const key of FREEZABLE_BUCKET_KEYS) {
      for (const it of grouped[key] || []) {
        ids.add(String(it.id))
      }
    }
    return ids
  }, [grouped])
  const selectedFreezableItems = useMemo(
    () =>
      Array.from(selectedItemIds)
        .filter((id) => freezableIdSet.has(id))
        .map((id) => itemById.get(id))
        .filter(Boolean),
    [itemById, freezableIdSet, selectedItemIds],
  )
  const canBulkFreeze = selectedFreezableItems.some((item) => !isFrozen(item))
  const canBulkThaw = selectedFreezableItems.some((item) => isFrozen(item))
  const freezableCount = selectedFreezableItems.filter((item) => !isFrozen(item)).length
  const thawableCount = selectedFreezableItems.filter((item) => isFrozen(item)).length
  const editActionsBusy = bulkRemoving || bulkFreezing
  const lowStock = totalItems > 0 && totalItems < LOW_STOCK_THRESHOLD
  peakPantryCountRef.current = computePeakPantryCount(peakPantryCountRef.current, totalItems)
  const isPantryShrinkingState = isPantryShrinking(totalItems, peakPantryCountRef.current)
  const existingStarterNames = [
    ...(grouped.proteins || []),
    ...(grouped.fruits_vegetables || []),
    ...(grouped.dairy || []),
    ...(grouped.spices || []),
    ...(grouped.staples || []),
  ].map((item) => item.item_name).filter(Boolean)
  const showPantryStarter = Boolean(onGuestOnboardingContinue) || (totalItems === 0 && !starterSessionDismissed)
  const showPantryStarterReopen = totalItems === 0 && starterSessionDismissed
  const showLowStockBanner = lowStock && isPantryShrinkingState && !showPantryStarter && totalItems > 0
  const hasExpiring = expiringItems.length > 0
  const suppressExpiringPromo = shouldSuppressExpiringPromo({
    suppressUntilMs: suppressExpiringPromoUntil,
    nowMs,
    recentlyAddedIds: lastAddedIds,
  })
  const showExpiringPromo = hasExpiring && !showPantryStarter && !suppressExpiringPromo

  const markPantryRecentlyAdded = useCallback(() => {
    setSuppressExpiringPromoUntil(Date.now() + EXPIRING_PROMO_SUPPRESS_MS)
  }, [])

  const dismissPantryStarterForSession = useCallback(() => {
    try {
      sessionStorage.setItem(PANTRY_STARTER_SESSION_KEY, '1')
    } catch {
      /* ignore */
    }
    setStarterSessionDismissed(true)
  }, [])

  const handleSkipPantryStarter = useCallback(() => {
    dismissPantryStarterForSession()
    onGuestOnboardingContinue?.()
  }, [dismissPantryStarterForSession, onGuestOnboardingContinue])

  const dismissExpiringBandForSession = useCallback(() => {
    try {
      sessionStorage.setItem(EXPIRING_BAND_DISMISS_SESSION_KEY, '1')
    } catch {
      /* ignore */
    }
    setExpiringBandDismissed(true)
  }, [])

  const reopenPantryStarter = useCallback(() => {
    try {
      sessionStorage.removeItem(PANTRY_STARTER_SESSION_KEY)
    } catch {
      /* ignore */
    }
    setStarterSessionDismissed(false)
  }, [])

  const handleAddRunningLowToShop = useCallback(
    async (item) => {
      const itemId = item?.id != null ? String(item.id) : ''
      const name = pantryItemDisplayName(item)
      if (!name || !itemId) return
      if (addingToShopIdsRef.current.has(itemId)) return

      addingToShopIdsRef.current.add(itemId)
      setAddingToShopIds(new Set(addingToShopIdsRef.current))

      try {
        // Omit pantryItemNames so in-stock (but running-low) items are still restocked.
        const { listId, addedCount } = await appendRecipeIngredientsToCurrentShoppingList(
          [{ name }],
          { authenticatedFetch },
        )
        notifySavedListsInvalidated()
        try {
          localStorage.setItem('pending_chat_saved_list_id', listId)
        } catch {
          // ignore quota / private-mode failures
        }
        window.dispatchEvent(new CustomEvent('loadSavedList', { detail: { listId } }))

        if (addedCount > 0) {
          showToast({
            title: `Added ${name} to Shop`,
            message: 'On your shopping list.',
            variant: 'success',
          })
        } else {
          showToast({
            title: `${name} is already on your list`,
            message: 'No duplicate added.',
            variant: 'success',
          })
        }
      } catch (err) {
        showToast({
          title: 'Could not add to shopping list',
          message: err?.message || 'Please try again.',
          variant: 'error',
          duration: 7000,
        })
      } finally {
        addingToShopIdsRef.current.delete(itemId)
        setAddingToShopIds(new Set(addingToShopIdsRef.current))
      }
    },
    [showToast],
  )

  const handleOpenReceiptTab = useCallback(() => {
    openAddPanel('receipt')
  }, [openAddPanel])

  const handleStarterBatchSuccess = useCallback(
    async ({ added, updated, itemIds, totalSelected }) => {
      setLastResult({ added, updated, not_imported: [] })
      if (itemIds.length > 0) setLastAddedIds(itemIds)
      if ((added ?? 0) > 0 || (updated ?? 0) > 0) markPantryRecentlyAdded()
      if (totalSelected >= 15) {
        try {
          sessionStorage.removeItem(PANTRY_STARTER_SESSION_KEY)
        } catch {
          /* ignore */
        }
        setStarterSessionDismissed(false)
      }
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      await refresh()
      notifyPantryInventoryChanged()
      listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      showToast({
        title: 'Pantry saved',
        message: `${totalSelected} ${totalSelected === 1 ? 'item' : 'items'} ready for planning.`,
        duration: 2800,
      })
      onGuestOnboardingContinue?.()
    },
    [markPantryRecentlyAdded, onGuestOnboardingContinue, refresh, showToast],
  )

  const exitEditMode = useCallback(() => {
    setEditMode(false)
    setBulkRemoveConfirm(false)
    setSelectedItemIds(new Set())
  }, [])

  const handleToggleSelectedItem = useCallback((itemId) => {
    if (!itemId) return
    setSelectedItemIds((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }, [])

  const showUndoToast = useCallback((names, label) => {
    setUndoToast({ names: [...names], label, count: names.length })
  }, [])

  const dismissUndoToast = useCallback(() => setUndoToast(null), [])

  const handleUndoRemove = useCallback(async () => {
    if (!undoToast?.names?.length) {
      dismissUndoToast()
      return
    }
    try {
      const res = await authenticatedFetch('/inventory/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_names: undoToast.names }),
      })
      let data = {}
      try {
        data = await parseJsonResponse(res)
      } catch {
        /* ignore */
      }
      if (!res.ok) throw new Error(data.detail || 'Failed to restore items')
      dismissUndoToast()
      await refresh()
      notifyPantryInventoryChanged()
    } catch (e) {
      setLastResult({ error: e.message || 'Could not undo removal' })
      dismissUndoToast()
    }
  }, [dismissUndoToast, refresh, undoToast])

  const removeItemsByIds = useCallback(
    async (ids, { names, toastLabel }) => {
      if (!ids.length) return
      setBulkRemoving(true)
      try {
        const res = await authenticatedFetch('/inventory/bulk-delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_ids: ids }),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to remove items')
        const removed = data.removed ?? ids.length
        const idSet = new Set(ids.map(String))
        // Drop any prior "added" feedback for items that are now gone.
        setLastAddedIds((prev) => prev.filter((id) => !idSet.has(String(id))))
        setLastAddedNames([])
        exitEditMode()
        await refresh()
        notifyPantryInventoryChanged()
        // Prefer the undo toast (auto-dismisses). Fall back to the inline banner when
        // we have no item names to restore.
        if (names?.length) {
          setLastResult(null)
          showUndoToast(names, toastLabel)
        } else {
          setLastResult({ removed })
        }
      } catch (e) {
        setLastResult({ error: e.message || 'Could not remove items' })
      } finally {
        setBulkRemoving(false)
      }
    },
    [exitEditMode, refresh, showUndoToast],
  )

  const confirmBulkRemoveItems = useCallback(async () => {
    const ids = Array.from(selectedItemIds)
    const names = ids.map((id) => pantryItemDisplayName(itemById.get(id))).filter(Boolean)
    const activeCat = PANTRY_CATEGORIES.find((c) => c.key === pantryActiveTab)
    await removeItemsByIds(ids, { names, toastLabel: activeCat?.label || 'items' })
    setBulkRemoveConfirm(false)
  }, [itemById, pantryActiveTab, removeItemsByIds, selectedItemIds])

  const handleSwipeRemove = useCallback(
    async (itemId) => {
      const item = itemById.get(itemId)
      const name = pantryItemDisplayName(item)
      await removeItemsByIds([itemId], { names: name ? [name] : [], toastLabel: 'item' })
    },
    [itemById, removeItemsByIds],
  )

  const stopQuantityEdit = useCallback(() => {
    setEditingItemId(null)
    confirmQtyEditRef.current = null
  }, [])

  const stopNameEdit = useCallback(() => {
    setEditingNameItemId(null)
  }, [])

  const startQuantityEdit = useCallback((itemId) => {
    if (!itemId) return
    if (confirmQtyEditRef.current && editingItemId && editingItemId !== itemId) {
      confirmQtyEditRef.current()
    }
    setEditingNameItemId(null)
    setEditingItemId(itemId)
  }, [editingItemId])

  const startNameEdit = useCallback((itemId) => {
    if (!itemId) return
    if (confirmQtyEditRef.current && editingItemId) {
      confirmQtyEditRef.current()
    }
    setEditingItemId(null)
    confirmQtyEditRef.current = null
    setEditingNameItemId(itemId)
  }, [editingItemId])

  const renameSelectedItem = useCallback(() => {
    if (selectedItemIds.size !== 1) return
    const itemId = Array.from(selectedItemIds)[0]
    if (!itemId) return
    exitEditMode()
    startNameEdit(itemId)
  }, [exitEditMode, selectedItemIds, startNameEdit])

  const registerQuantityConfirm = useCallback((fn) => {
    confirmQtyEditRef.current = fn
  }, [])

  const handleDisplayNameChange = useCallback(
    async (item, nextName) => {
      if (!item?.id) return
      const itemId = String(item.id)
      const displayName = (nextName || '').trim()
      if (!displayName) return
      if (displayName === pantryItemDisplayName(item)) return

      setNameBusyIds((prev) => {
        const next = new Set(prev)
        next.add(itemId)
        return next
      })
      try {
        const res = await authenticatedFetch(`/inventory/${itemId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: displayName }),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to rename item')
        await refresh()
      } catch (e) {
        setLastResult({ error: e.message || 'Could not rename item' })
      } finally {
        setNameBusyIds((prev) => {
          const next = new Set(prev)
          next.delete(itemId)
          return next
        })
      }
    },
    [refresh],
  )

  const handleQuantityChange = useCallback(
    async (item, nextQuantity, nextUnit) => {
      if (!item?.id) return
      const itemId = String(item.id)

      // Amount 0 means the item is gone — remove it and clear any "added" banner.
      if (nextQuantity == null || nextQuantity <= 0) {
        const name = pantryItemDisplayName(item)
        await removeItemsByIds([itemId], {
          names: name ? [name] : [],
          toastLabel: 'item',
        })
        return
      }

      const quantity = Math.floor(Number(nextQuantity))
      if (!Number.isFinite(quantity) || quantity < 1) return

      setQuantityBusyIds((prev) => {
        const next = new Set(prev)
        next.add(itemId)
        return next
      })
      try {
        const body = { quantity }
        if (nextUnit !== undefined) body.quantity_unit = nextUnit
        const res = await authenticatedFetch(`/inventory/${itemId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to update amount')
        await refresh()
        notifyPantryInventoryChanged()
      } catch (e) {
        setLastResult({ error: e.message || 'Could not update amount' })
      } finally {
        setQuantityBusyIds((prev) => {
          const next = new Set(prev)
          next.delete(itemId)
          return next
        })
      }
    },
    [refresh, removeItemsByIds],
  )

  const handleBulkSetFrozen = useCallback(
    async (frozen) => {
      const ids = Array.from(selectedItemIds).filter((id) => {
        if (!freezableIdSet.has(id)) return false
        const item = itemById.get(id)
        if (!item) return false
        return frozen ? !isFrozen(item) : isFrozen(item)
      })
      if (!ids.length) return
      setBulkFreezing(true)
      try {
        const res = await authenticatedFetch('/inventory/bulk-frozen', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_ids: ids, frozen: Boolean(frozen) }),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to update freezer status')
        setSelectedItemIds(new Set())
        await refresh()
        notifyPantryInventoryChanged()
      } catch (e) {
        setLastResult({ error: e.message || 'Could not update freezer status' })
      } finally {
        setBulkFreezing(false)
      }
    },
    [freezableIdSet, itemById, refresh, selectedItemIds],
  )

  const manualAddIsBatch = useMemo(() => {
    const normalized = normalizePantryListInput(manualItemName).trim()
    if (!normalized) return false
    const parts = normalized
      .split(/[\n,;|•]+/g)
      .map((s) => (s || '').trim())
      .filter(Boolean)
    return parts.length > 1
  }, [manualItemName])

  /** Expand destination category sections (desktop) and select the first tab (mobile). */
  const expandCategoryBuckets = useCallback((bucketKeys) => {
    const keys = [...new Set((bucketKeys || []).filter(Boolean))]
    if (keys.length === 0) return
    setPantryActiveTab(keys[0])
    setAccordionExpanded((prev) => {
      const next = { ...prev }
      for (const key of keys) next[key] = true
      return next
    })
  }, [])

  const openReceiptPreview = (data, { fromPdf }) => {
    const candidates = Array.isArray(data.candidates) ? data.candidates : []
    const notImported = data.not_imported || []
    if (candidates.length === 0) {
      setReceiptFromPdf(false)
      setReceiptInputNotice({ notice: fromPdf ? 'zero_candidates_pdf' : 'zero_candidates' })
      setReceiptUnrecognized(notImported)
      return
    }
    setReceiptFromPdf(fromPdf)
    setReceiptCandidates(candidates)
    setReceiptUnrecognized(notImported)
    setReceiptCommitError(null)
    setReceiptReviewing(true)
  }

  const handleReviewReceipt = async () => {
    const text = (receiptText || '').trim()
    if (!text) return
    setSubmitLoading(true)
    setReceiptBusyKind('paste')
    setLastResult(null)
    setReceiptInputNotice(null)
    setLastAddedIds([])
    setLastAddedNames([])
    try {
      const res = await authenticatedFetch('/inventory/from-receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receipt_text: text, preview: true }),
      })
      let data = {}
      try {
        data = await parseJsonResponse(res)
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : ''
        throw new Error(
          detail || "We couldn't process that text. Your paste hasn't been lost — try again.",
        )
      }
      openReceiptPreview(data, { fromPdf: false })
    } catch (e) {
      setReceiptInputNotice({
        error: e.message || "We couldn't process that text. Your paste hasn't been lost — try again.",
      })
    } finally {
      setSubmitLoading(false)
      setReceiptBusyKind(null)
    }
  }

  const handleReceiptPdfSelected = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')
    if (!isPdf) {
      setReceiptInputNotice({ error: 'Choose a PDF invoice.' })
      return
    }
    setSubmitLoading(true)
    setReceiptBusyKind('pdf')
    setLastResult(null)
    setReceiptInputNotice(null)
    setLastAddedIds([])
    setLastAddedNames([])
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await authenticatedFetch('/inventory/from-receipt-pdf', {
        method: 'POST',
        body: form,
      })
      let data = {}
      try {
        data = await parseJsonResponse(res)
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        const detail = typeof data.detail === 'string' ? data.detail : ''
        throw new Error(detail || "We couldn't read that PDF. Paste the receipt instead.")
      }
      openReceiptPreview(data, { fromPdf: true })
    } catch (e) {
      setReceiptInputNotice({
        error: e.message || "We couldn't read that PDF. Paste the receipt instead.",
      })
    } finally {
      setSubmitLoading(false)
      setReceiptBusyKind(null)
    }
  }

  const handleCommitReceiptReview = useCallback(
    async (checkedItems) => {
      if (!checkedItems?.length) return
      setReceiptCommitLoading(true)
      setReceiptCommitError(null)
      setLastResult(null)
      setLastAddedIds([])
      setLastAddedNames([])
      try {
        const payloadItems = checkedItems.map((item) => {
          const baseName = (item.name || '').trim()
          const measure = (item.measure_display || '').trim()
          let name = baseName
          // Fractional weights can't use integer quantity; reattach onto the name at commit.
          if (measure) {
            const alreadyHas = new RegExp(
              `\\b${measure.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
              'i',
            ).test(baseName)
            const capturedAsIntQty =
              item.quantity != null &&
              item.quantity_unit &&
              `${item.quantity} ${item.quantity_unit}`.toLowerCase() === measure.toLowerCase()
            if (!alreadyHas && !capturedAsIntQty) {
              name = `${baseName} ${measure}`.trim()
            }
          }
          return {
            name,
            quantity: item.quantity ?? undefined,
            quantity_unit: item.quantity_unit || undefined,
            category: item.category_token || undefined,
          }
        })
        const res = await authenticatedFetch('/inventory/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: payloadItems }),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || "Couldn't add these items")
        const items = Array.isArray(data?.items) ? data.items : []
        const ids = items.map((i) => i?.id).filter(Boolean).map((id) => String(id))
        const names = items.map((i) => pantryItemDisplayName(i) || i?.item_name).filter(Boolean)
        const failed = data?.failed ?? (data?.failed_items?.length ?? 0)
        expandCategoryBuckets(bucketKeysFromItems(items))
        setReceiptText('')
        resetReceiptReview()
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
        await refresh()
        notifyPantryInventoryChanged()
        if ((data?.added ?? 0) > 0 || (data?.updated ?? 0) > 0) markPantryRecentlyAdded()
        if (showPantryStarter) {
          if (names.length > 0) setStarterAddedNames(names)
        } else {
          setLastResult({
            added: data?.added ?? 0,
            updated: data?.updated ?? 0,
            failed,
            not_imported: [],
          })
          if (ids.length > 0) setLastAddedIds(ids)
          if (names.length > 0) setLastAddedNames(names)
          if (ids.length > 0) listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } catch (e) {
        setReceiptCommitError(e.message || 'Your reviewed list is still here — nothing was lost.')
      } finally {
        setReceiptCommitLoading(false)
      }
    },
    [expandCategoryBuckets, markPantryRecentlyAdded, refresh, resetReceiptReview, showPantryStarter],
  )

  const addSingleInventoryItem = useCallback(
    async ({ itemName, quantity, quantity_unit, category, bucketKey }) => {
      const body = { item_name: itemName }
      if (quantity != null) body.quantity = quantity
      if (quantity != null && quantity_unit) body.quantity_unit = quantity_unit
      if (category) body.category = category
      const res = await authenticatedFetch('/inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      let data = {}
      try {
        data = await parseJsonResponse(res)
      } catch {
        /* ignore */
      }
      if (!res.ok) throw new Error(data.detail || 'Failed to add item')
      setLastResult({ added: 1, updated: 0, not_imported: [] })
      if (data?.id != null) setLastAddedIds([String(data.id)])
      setLastAddedNames([itemName])
      markPantryRecentlyAdded()
      expandCategoryBuckets([bucketKey || categoryToBucketKey(data?.category ?? category)])
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      await refresh()
      notifyPantryInventoryChanged()
      return data
    },
    [expandCategoryBuckets, markPantryRecentlyAdded, refresh],
  )

  const handleAddManualItem = async () => {
    const text = (manualItemName || '').trim()
    if (!text) return
    const normalized = normalizePantryListInput(text).trim()
    if (!normalized) return
    const parts = normalized
      .split(/[\n,;|•]+/g)
      .map((s) => (s || '').trim())
      .filter(Boolean)
    const isBatch = parts.length > 1
    setManualAddLoading(true)
    setLastResult(null)
    setLastAddedIds([])
    setLastAddedNames([])
    try {
      if (isBatch) {
        const res = await authenticatedFetch('/inventory/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ item_names: parts }),
        })
        let data = {}
        try {
          data = await parseJsonResponse(res)
        } catch {
          /* ignore */
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to add item')
        const items = Array.isArray(data?.items) ? data.items : []
        const ids = items.map((i) => i?.id).filter(Boolean).map((id) => String(id))
        const names = items.map((i) => pantryItemDisplayName(i) || i?.item_name).filter(Boolean)
        setLastResult({ added: data?.added ?? 0, updated: data?.updated ?? 0, not_imported: [] })
        if (ids.length > 0) setLastAddedIds(ids)
        if (names.length > 0) setLastAddedNames(names)
        expandCategoryBuckets(bucketKeysFromItems(items))
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))
        await refresh()
        notifyPantryInventoryChanged()
        if ((data?.added ?? 0) > 0 || (data?.updated ?? 0) > 0) markPantryRecentlyAdded()
      } else {
        const resolved = resolvePantryAddAmount(parts[0], {
          amount: manualAddQuantity,
          unit: manualAddUnit,
        })
        await addSingleInventoryItem({
          itemName: resolved.name || parts[0],
          quantity: resolved.quantity != null && resolved.quantity > 0 ? resolved.quantity : undefined,
          quantity_unit: resolved.quantity_unit || undefined,
        })
      }
      setManualItemName('')
      setManualAddQuantity('')
      setManualAddUnit('')
      if (!showPantryStarter) {
        listSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (e) {
      setLastResult({ error: e.message })
    } finally {
      setManualAddLoading(false)
    }
  }

  const handleInlineAddChange = useCallback((bucketKey, value) => {
    setInlineAddByBucket((prev) => ({ ...prev, [bucketKey]: value }))
  }, [])

  const handleInlineAdd = useCallback(
    async (bucketKey) => {
      const text = (inlineAddByBucket[bucketKey] || '').trim()
      if (!text) return false
      const normalized = normalizePantryListInput(text).trim()
      if (!normalized) return false
      const first = normalized
        .split(/[\n,;|•]+/g)
        .map((s) => (s || '').trim())
        .filter(Boolean)[0]
      if (!first) return false
      const resolved = resolvePantryAddAmount(first)
      const category = BUCKET_TO_CATEGORY[bucketKey]
      setInlineAddLoadingBucket(bucketKey)
      setLastResult(null)
      setLastAddedIds([])
      setLastAddedNames([])
      try {
        await addSingleInventoryItem({
          itemName: resolved.name || first,
          quantity: resolved.quantity != null && resolved.quantity > 0 ? resolved.quantity : undefined,
          quantity_unit: resolved.quantity_unit || undefined,
          category,
          bucketKey,
        })
        setInlineAddByBucket((prev) => ({ ...prev, [bucketKey]: '' }))
        return true
      } catch (e) {
        setLastResult({ error: e.message })
        return false
      } finally {
        setInlineAddLoadingBucket(null)
      }
    },
    [addSingleInventoryItem, inlineAddByBucket],
  )

  const enterEditMode = useCallback(
    (fromBucket) => {
      if (confirmQtyEditRef.current) confirmQtyEditRef.current()
      else stopQuantityEdit()
      stopNameEdit()
      setLastResult(null)
      // Clear in-progress receipt review when entering select mode.
      if (isMobile) {
        resetReceiptReview()
        setReceiptInputNotice(null)
      }
      setEditMode(true)
      if (!isMobile) {
        setAccordionExpanded({
          proteins: true,
          fruits_vegetables: true,
          dairy: true,
          spices: true,
          staples: true,
        })
      } else if (fromBucket) {
        setPantryActiveTab(fromBucket)
      }
    },
    [isMobile, stopNameEdit, stopQuantityEdit, resetReceiptReview],
  )

  const tabsLocked = receiptReviewing
  const selectAddTab = (tab) => {
    if (tabsLocked) return
    setAddMethodTab(tab)
    setReceiptInputNotice(null)
  }

  const receiptLineCount = (receiptText || '').split(/\n/).filter((l) => l.trim()).length

  const queueStarterItems = (addExtraItems) => {
    const text = (manualItemName || '').trim()
    if (!text || typeof addExtraItems !== 'function') return
    const normalized = normalizePantryListInput(text).trim()
    if (!normalized) return
    const segments = normalized
      .split(/[\n,;|•]+/g)
      .map((part) => (part || '').trim())
      .filter(Boolean)
    const single = segments.length === 1
    const names = segments
      .map((trimmed) => {
        const resolved = resolvePantryAddAmount(
          trimmed,
          single ? { amount: manualAddQuantity, unit: manualAddUnit } : {},
        )
        return formatPantryAddLine(resolved)
      })
      .filter(Boolean)
    if (names.length === 0) return
    const added = addExtraItems(names)
    setStarterAddedNames(Array.isArray(added) && added.length > 0 ? added : names)
    setManualItemName('')
    setManualAddQuantity('')
    setManualAddUnit('')
  }

  const renderAddItemsFields = (onAdd = handleAddManualItem, { showQuantity = true } = {}) => (
    <div>
      <div className="pantry-v2__add-row">
        <Input
          type="text"
          name="pantry-add-items"
          inputMode="text"
          autoComplete="off"
          value={manualItemName}
          onChange={(e) => setManualItemName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (!manualItemName.trim() || manualAddLoading) return
            onAdd()
          }}
          placeholder="milk, eggs, butter — separate with commas"
          aria-label="Item name"
          className="pantry-v2__add-input"
        />
        {showQuantity && !manualAddIsBatch ? (
          <div className="pantry-v2__add-amount">
            <Input
              id="pantry-add-qty"
              type="number"
              inputMode="numeric"
              min={1}
              value={manualAddQuantity}
              onChange={(e) => setManualAddQuantity(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                if (!manualItemName.trim() || manualAddLoading) return
                onAdd()
              }}
              placeholder="2"
              aria-label="Amount"
              className="pantry-v2__add-qty-input"
            />
            <PantryUnitSelect
              className="pantry-v2__add-unit"
              value={manualAddUnit}
              ariaLabel="Unit"
              disabled={manualAddLoading}
              onChange={(e) => setManualAddUnit(e.target.value)}
            />
          </div>
        ) : null}
        <Button
          variant="primary"
          onClick={onAdd}
          disabled={!manualItemName.trim() || manualAddLoading}
        >
          {manualAddLoading ? 'Adding…' : 'Add'}
        </Button>
      </div>
      <PantrySpellingSuggestions
        inputValue={manualItemName}
        onApplySuggestion={setManualItemName}
      />
    </div>
  )

  const receiptFields = (ctaVariant = 'primary') => (
    <div className="pantry-v2__receipt-zone">
      {receiptInputNotice ? (
        <PantryAddFeedbackBanner
          lastResult={receiptInputNotice}
          onDismiss={() => setReceiptInputNotice(null)}
          onTryManual={
            receiptInputNotice?.notice === 'zero_candidates'
              ? () => {
                  setReceiptInputNotice(null)
                  handleReviewReceipt()
                }
              : undefined
          }
        />
      ) : null}
      <textarea
        name="pantry-add-receipt"
        value={receiptText}
        onChange={(e) => setReceiptText(e.target.value)}
        placeholder="Paste receipt or order confirmation text here…"
        rows={4}
        className="pantry-v2__receipt-textarea"
        aria-label="Receipt text"
        disabled={submitLoading}
      />
      <div className="pantry-v2__receipt-upload">
        <input
          ref={receiptPdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="sr-only"
          aria-label="Upload invoice PDF"
          onChange={handleReceiptPdfSelected}
          disabled={submitLoading}
        />
        <Button
          variant="secondary"
          onClick={() => receiptPdfInputRef.current?.click()}
          disabled={submitLoading}
        >
          {receiptBusyKind === 'pdf' ? 'Reading invoice…' : 'Upload invoice'}
        </Button>
        <p className="pantry-v2__receipt-upload-hint">
          A PDF from the store. You&apos;ll review items before they&apos;re added.
        </p>
      </div>
      <Button
        variant={ctaVariant}
        onClick={handleReviewReceipt}
        disabled={!receiptText.trim() || submitLoading}
      >
        {receiptBusyKind === 'paste' ? 'Reviewing…' : 'Review items →'}
      </Button>
    </div>
  )

  const visionStub = (
    <div className="pantry-v2__vision-stub">
      <div className="pantry-v2__vision-stub-title">Coming soon</div>
      <p className="pantry-v2__vision-stub-body">
        Photo scan of your shelf — will route through the same Review &amp; Confirm step as
        receipts.
      </p>
    </div>
  )

  const receiptReviewPanel = (
    <PantryReviewConfirm
      ref={reviewConfirmRef}
      source="receipt"
      backLabel={receiptFromPdf ? 'Back' : ''}
      sourcePreview={
        <span className="pantry-review-confirm__source-preview">
          {receiptFromPdf
            ? 'Invoice PDF'
            : `Pasted ${receiptLineCount || 1} line${receiptLineCount === 1 ? '' : 's'}`}
        </span>
      }
      candidates={receiptCandidates}
      unrecognized={receiptUnrecognized}
      onBack={handleReceiptBackToInput}
      onClose={() => {
        resetReceiptReview()
        setReceiptInputNotice(null)
      }}
      onCommit={handleCommitReceiptReview}
      onEmptyReturn={handleReceiptEmptyReturn}
      committing={receiptCommitLoading}
      commitError={receiptCommitError}
      onClearCommitError={() => setReceiptCommitError(null)}
    />
  )

  const starterExtrasPanel = ({ addExtraItems } = {}) => (receiptReviewing ? (
    receiptReviewPanel
  ) : (
    <div className="pantry-quick-add__extras-stack">
      <section className="pantry-quick-add__extras-block">
        <h3 className="pantry-quick-add__extras-heading u-text-card-title">Add anything else</h3>
        <p className="pantry-quick-add__extras-copy">
          Add anything that wasn&apos;t on the lists.
        </p>
        {renderAddItemsFields(() => queueStarterItems(addExtraItems))}
        {starterAddedNames.length > 0 ? (
          <p className="pantry-quick-add__added" role="status">
            Added {formatAddedList(starterAddedNames)}.
          </p>
        ) : null}
      </section>
      <section className="pantry-quick-add__extras-block pantry-quick-add__extras-block--receipt">
        <details className="pantry-quick-add__receipt-disclosure">
          <summary className="pantry-quick-add__extras-heading">
            Or add a receipt
            <i className="fas fa-chevron-down pantry-quick-add__receipt-caret" aria-hidden="true" />
          </summary>
          {receiptFields('secondary')}
        </details>
      </section>
    </div>
  ))

  // Add stays exposed on desktop and mobile (including during select mode).
  const addPanel = (
    <div className="pantry-v2__add-panel pantry-v2__add-panel--inventory">
      {receiptReviewing ? (
        receiptReviewPanel
      ) : (
        <>
          <div
            role="tablist"
            aria-label="Add to pantry"
            className="cook-sub-tab-bar"
          >
            <button
              type="button"
              role="tab"
              aria-selected={addMethodTab === 'item'}
              id="pantry-add-tab-item"
              aria-controls="pantry-add-panel-item"
              onClick={() => selectAddTab('item')}
              disabled={tabsLocked}
              title={tabsLocked ? 'Finish reviewing before switching tabs' : undefined}
              className={`cook-sub-tab ${addMethodTab === 'item' ? 'active' : ''}`}
            >
              <span className="cook-sub-tab__label">Add items</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={addMethodTab === 'receipt'}
              id="pantry-add-tab-receipt"
              aria-controls="pantry-add-panel-receipt"
              onClick={() => selectAddTab('receipt')}
              disabled={tabsLocked}
              title={tabsLocked ? 'Finish reviewing before switching tabs' : undefined}
              className={`cook-sub-tab ${addMethodTab === 'receipt' ? 'active' : ''}`}
            >
              <span className="cook-sub-tab__label">From receipt</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={addMethodTab === 'vision'}
              id="pantry-add-tab-vision"
              aria-controls="pantry-add-panel-vision"
              onClick={() => selectAddTab('vision')}
              disabled={tabsLocked}
              title={tabsLocked ? 'Finish reviewing before switching tabs' : undefined}
              className={`cook-sub-tab ${addMethodTab === 'vision' ? 'active' : ''}`}
            >
              <span className="cook-sub-tab__label">
                Snap a shelf <span className="pantry-v2__soon-chip">soon</span>
              </span>
            </button>
          </div>
          <div
            className="pantry-v2__add-body"
            role="tabpanel"
            id={
              addMethodTab === 'item'
                ? 'pantry-add-panel-item'
                : addMethodTab === 'receipt'
                  ? 'pantry-add-panel-receipt'
                  : 'pantry-add-panel-vision'
            }
            aria-labelledby={
              addMethodTab === 'item'
                ? 'pantry-add-tab-item'
                : addMethodTab === 'receipt'
                  ? 'pantry-add-tab-receipt'
                  : 'pantry-add-tab-vision'
            }
          >
            {addMethodTab === 'item'
              ? renderAddItemsFields()
              : addMethodTab === 'receipt'
                ? receiptFields()
                : visionStub}
          </div>
        </>
      )}
    </div>
  )

  const editToolbar =
    editMode && totalItems > 0 && !showPantryStarter ? (
      <div className="pantry-edit-toolbar pantry-edit-toolbar-active">
        <span className="pantry-edit-toolbar-status">
          {selectedCount > 0
            ? `${selectedCount} selected`
            : isMobile
              ? 'Tap items to select — switch categories anytime'
              : 'Click items to select across any category'}
        </span>
        <div className="pantry-edit-toolbar-actions">
          <button
            type="button"
            className="pantry-danger-btn"
            onClick={() => setBulkRemoveConfirm(true)}
            disabled={selectedCount === 0 || editActionsBusy}
          >
            Remove ({selectedCount})
          </button>
          {selectedCount === 1 ? (
            <button
              type="button"
              className="secondary"
              onClick={renameSelectedItem}
              disabled={editActionsBusy}
              title="Rename the selected item"
            >
              Rename
            </button>
          ) : null}
          {canBulkFreeze ? (
            <button
              type="button"
              className="secondary"
              onClick={() => handleBulkSetFrozen(true)}
              disabled={editActionsBusy}
              title="Mark selected items as frozen (freezer storage; pauses fridge countdown when perishable)"
            >
              {bulkFreezing ? 'Updating…' : `Freeze (${freezableCount})`}
            </button>
          ) : null}
          {canBulkThaw ? (
            <button
              type="button"
              className="secondary"
              onClick={() => handleBulkSetFrozen(false)}
              disabled={editActionsBusy}
              title="Clear frozen label on selected items (restarts fridge countdown when perishable)"
            >
              {`Thaw (${thawableCount})`}
            </button>
          ) : null}
          <button type="button" className="ghost" onClick={exitEditMode} disabled={editActionsBusy}>
            Done
          </button>
        </div>
      </div>
    ) : null

  const sidebar = !showPantryStarter ? (
    <aside className="pantry-v2__sidebar">
      {showExpiringPromo ? (
        <section className="pantry-use-it-sidebar" aria-labelledby="pantry-use-it-heading">
          <p className="pantry-use-it-sidebar__eyebrow">Use soon</p>
          <h2 id="pantry-use-it-heading" className="pantry-use-it-sidebar__title">
            {expiringItems.length} item{expiringItems.length !== 1 ? 's' : ''} need a plan
          </h2>
          {onAskAI ? (
            <button type="button" className="primary pantry-use-it-sidebar__cta" onClick={onAskAI}>
              Plan around these →
            </button>
          ) : null}
          <div className="pantry-use-it-sidebar__tiles">
            {expiringItems.slice(0, 6).map((item) => (
              <ExpiringTile key={item.id} item={item} />
            ))}
          </div>
        </section>
      ) : null}

      {runningLowItems.length > 0 ? (
        <section className="pantry-sidebar-card">
          <h3 className="pantry-sidebar-card__title">Running low</h3>
          {runningLowItems.slice(0, 6).map((item) => {
            const itemId = String(item.id)
            const displayName = pantryItemDisplayName(item)
            const adding = addingToShopIds.has(itemId)
            return (
              <div key={item.id} className="pantry-running-low-row">
                <span className="pantry-running-low-row__name">{displayName}</span>
                <span className="pantry-running-low-row__qty">{formatPantryQuantity(item)} left</span>
                <button
                  type="button"
                  className="pantry-running-low-row__shop"
                  onClick={() => handleAddRunningLowToShop(item)}
                  disabled={adding}
                  aria-label={`Add ${displayName} to shopping list`}
                  title={`Add ${displayName} to shopping list`}
                >
                  {adding ? 'Adding…' : '+ Shop'}
                </button>
              </div>
            )
          })}
          <p className="pantry-running-low-note">
            Shows items depleted from their original amount (e.g. 3 lemons → 1, or 10 lb → 2 lb)
          </p>
        </section>
      ) : null}

      <section className="pantry-sidebar-ai">
        <div className="pantry-sidebar-ai__head">
          <span className="pantry-sidebar-ai__icon" aria-hidden="true">
            ✦
          </span>
          <div>
            <div className="pantry-sidebar-ai__title">Just got groceries?</div>
            <p className="pantry-sidebar-ai__body">
              Paste a receipt — you&apos;ll review what we found before anything is added.
            </p>
            <button type="button" className="pantry-sidebar-ai__link" onClick={handleOpenReceiptTab}>
              Paste receipt text →
            </button>
          </div>
        </div>
      </section>
    </aside>
  ) : null

  const mobileExpiringBand =
    isMobile && showExpiringPromo && !expiringBandDismissed ? (
      <section className="pantry-expiring-band" aria-labelledby="pantry-expiring-band-heading">
        <div className="pantry-expiring-band__head">
          <div>
            <p className="pantry-expiring-band__eyebrow">Use soon</p>
            <h2 id="pantry-expiring-band-heading" className="pantry-expiring-band__title">
              {expiringItems.length} items need a plan
            </h2>
          </div>
          <div className="pantry-expiring-band__actions">
            {onAskAI ? (
              <button type="button" className="pantry-expiring-band__cta" onClick={onAskAI}>
                Plan →
              </button>
            ) : null}
            <button
              type="button"
              className="pantry-expiring-band__dismiss"
              onClick={dismissExpiringBandForSession}
              aria-label="Dismiss Use soon"
            >
              ×
            </button>
          </div>
        </div>
        <div className="pantry-expiring-band__tiles">
          {expiringItems.slice(0, 8).map((item) => (
            <ExpiringTile key={item.id} item={item} compact />
          ))}
        </div>
      </section>
    ) : null

  const hidePantryPageHeader =
    showPantryStarter && (isMobile || Boolean(onGuestOnboardingContinue))

  const activeMobileCategory =
    PANTRY_CATEGORIES.find(({ key }) => key === pantryActiveTab) || PANTRY_CATEGORIES[0]

  const inventoryContent = showPantryStarter ? (
    <div className="pantry-empty-grid">
      <div>
        <PantryQuickAddTabs
          onSkipForNow={onGuestOnboardingContinue ? undefined : handleSkipPantryStarter}
          onBatchSuccess={handleStarterBatchSuccess}
          variant={isMobile ? 'mobile' : 'desktop'}
          headingLevel={hidePantryPageHeader ? 1 : 2}
          existingItemNames={existingStarterNames}
          extrasContent={starterExtrasPanel}
        />
      </div>
    </div>
  ) : (
    <>
      {isMobile ? (
        <>
          {mobileExpiringBand}
          {!showPantryStarter ? (
            <div className="pantry-v2__inventory-toolbar-wrap" ref={inventoryToolbarRef}>
              {addPanel}
              {editMode ? editToolbar : null}
            </div>
          ) : null}
          {showPantryStarterReopen ? null : (
            <>
          <div className="pantry-v2__mobile-tabs" role="tablist" aria-label="Pantry categories">
            {PANTRY_CATEGORIES.map(({ key, label, icon }) => {
              const items = grouped[key] || []
              const tabHasExpiring = items.some((i) => isExpiringWithinDays(i, EXPIRING_BANNER_DAYS))
              const tabHasUrgent = items.some((i) => {
                const d = getDaysLeft(i)
                return d != null && d <= 2
              })
              const isActive = pantryActiveTab === key
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  className={`pantry-v2__mobile-tab${isActive ? ' pantry-v2__mobile-tab--active' : ''}`}
                  aria-selected={isActive}
                  onClick={() => setPantryActiveTab(key)}
                >
                  <span className="pantry-v2__mobile-tab-icon-row">
                    <span>{icon}</span>
                    {tabHasExpiring ? (
                      <span
                        className={`pantry-accordion__expiry-dot${tabHasUrgent ? ' pantry-accordion__expiry-dot--urgent' : ' pantry-accordion__expiry-dot--soon'}`}
                      />
                    ) : null}
                  </span>
                  <span className="pantry-v2__mobile-tab-label">{label}</span>
                  <span className="pantry-v2__mobile-tab-count">({items.length})</span>
                </button>
              )
            })}
          </div>
          <MobileCategoryPanel
            key={activeMobileCategory.key}
            label={activeMobileCategory.label}
            bucketKey={activeMobileCategory.key}
            hidden={false}
            listRef={listSectionRef}
            items={grouped[activeMobileCategory.key] || []}
            editMode={editMode}
            selectedItemIds={selectedItemIds}
            onToggleItem={handleToggleSelectedItem}
            recentlyAddedIds={lastAddedIds}
            receiptReviewing={receiptReviewing}
            onEnterEditMode={enterEditMode}
            onSwipeRemove={handleSwipeRemove}
            quantityBusyIds={quantityBusyIds}
            editingItemId={editingItemId}
            onStartEdit={startQuantityEdit}
            onStopEdit={stopQuantityEdit}
            onRegisterConfirm={registerQuantityConfirm}
            editingNameItemId={editingNameItemId}
            onStartNameEdit={startNameEdit}
            onStopNameEdit={stopNameEdit}
            onDisplayNameChange={handleDisplayNameChange}
            nameBusyIds={nameBusyIds}
            onQuantityChange={handleQuantityChange}
            inlineAddValue={inlineAddByBucket[activeMobileCategory.key] || ''}
            onInlineAddChange={handleInlineAddChange}
            onInlineAdd={handleInlineAdd}
            inlineAddLoading={inlineAddLoadingBucket === activeMobileCategory.key}
          />
            </>
          )}
        </>
      ) : (
        <div className="pantry-v2__main-grid" ref={listSectionRef}>
          <div>
            <div className="pantry-v2__inventory-toolbar-wrap" ref={inventoryToolbarRef}>
              {addPanel}
              {editMode ? editToolbar : null}
            </div>
            {showPantryStarterReopen ? null : (
            <div className="pantry-v2__accordions">
              {PANTRY_CATEGORIES.map(({ key, label, icon }) => (
                <CategoryAccordion
                  key={key}
                  bucketKey={key}
                  label={label}
                  icon={icon}
                  items={grouped[key] || []}
                  expanded={accordionExpanded[key]}
                  onToggle={() => setAccordionExpanded((p) => ({ ...p, [key]: !p[key] }))}
                  editMode={editMode}
                  selectedItemIds={selectedItemIds}
                  onToggleItem={handleToggleSelectedItem}
                  recentlyAddedIds={lastAddedIds}
                  isMobile={false}
                  showOverflowMenu
                  onSelectItems={enterEditMode}
                  selectItemsDisabled={receiptReviewing}
                  onQuantityChange={handleQuantityChange}
                  quantityBusyIds={quantityBusyIds}
                  editingItemId={editingItemId}
                  onStartEdit={startQuantityEdit}
                  onStopEdit={stopQuantityEdit}
                  onRegisterConfirm={registerQuantityConfirm}
                  editingNameItemId={editingNameItemId}
                  onStartNameEdit={startNameEdit}
                  onStopNameEdit={stopNameEdit}
                  onDisplayNameChange={handleDisplayNameChange}
                  nameBusyIds={nameBusyIds}
                  inlineAddValue={inlineAddByBucket[key] || ''}
                  onInlineAddChange={handleInlineAddChange}
                  onInlineAdd={handleInlineAdd}
                  inlineAddLoading={inlineAddLoadingBucket === key}
                />
              ))}
            </div>
            )}
          </div>
          {sidebar}
        </div>
      )}
    </>
  )

  if (!isAuthenticated) return null

  const showPantryIntro = ONBOARDING_BANNER_FLAGS.pantryIntro && isPantryIntroVisible
  const pantryPageHeading = isMobile ? 'Pantry' : 'Your pantry'
  const pantryPageEyebrow = getPantryPageEyebrow({
    totalItems,
    loading,
    contextEyebrow: getPantryContextEyebrow({
      loading,
      totalItems,
      showPantryStarter,
      lowStock,
      isPantryShrinkingState,
    }),
    fallbackEyebrow: isMobile ? 'Your pantry' : 'Pantry',
  })

  const pantryPageHeadingBlock = (
    <div className="pantry-v2__page-heading">
      <div className="page-intro-eyebrow-slot">
        <p className="eyebrow planner-eyebrow-tight">{pantryPageEyebrow}</p>
      </div>
      <h1 className="pantry-v2__title">{pantryPageHeading}</h1>
    </div>
  )

  const headerBlock = (
    <div className="pantry-v2__header">
      <div className="pantry-v2__header-main">
        {pantryPageHeadingBlock}
        {totalItems > 0 ? (
          isMobile ? (
            <>
              <div className="pantry-v2__mobile-stat-chips">
                <div
                  className={`pantry-v2__mobile-stat-chip${
                    hasExpiring ? ' pantry-v2__mobile-stat-chip--expiring' : ' pantry-v2__mobile-stat-chip--fresh'
                  }`}
                >
                  <div className="pantry-v2__mobile-stat-label">Expiring</div>
                  <div className="pantry-v2__mobile-stat-value">{hasExpiring ? expiringItems.length : 0}</div>
                </div>
              </div>
              <hr className="pantry-v2__stat-divider" aria-hidden="true" />
            </>
          ) : (
            <div className="pantry-v2__stats-line">
              {hasExpiring ? (
                <span className="pantry-v2__expiring-count">
                  <strong>{expiringItems.length}</strong> to use in the next few days
                </span>
              ) : (
                <span className="pantry-v2__fresh-status">
                  <span className="pantry-v2__fresh-dot" aria-hidden="true" />
                  Nothing needs using soon
                </span>
              )}
            </div>
          )
        ) : null}
      </div>
    </div>
  )

  // Guest setup draws the local checklist. The regular pantry skeleton waits on
  // grouped inventory, which must not block that first screen.
  if (loading && !onGuestOnboardingContinue) {
    return (
      <div className="tab-content-wrapper">
        <div className="pantry-tab-content pantry-v2">
          {pantryPageHeadingBlock}
          {showPantryIntro ? (
            <DismissibleOnboardingMessage
              messageId="pantryIntro"
              className="onboarding-message-left"
              isVisible={showPantryIntro}
            />
          ) : null}
          <PantrySkeleton isMobile={isMobile} />
        </div>
      </div>
    )
  }

  return (
    <div className="tab-content-wrapper">
      <div className={`pantry-tab-content pantry-v2${showPantryStarter && isMobile ? ' pantry-v2--starter-mobile' : ''}`}>
          {hidePantryPageHeader ? null : headerBlock}

          <DismissibleOnboardingMessage
            messageId="pantryIntro"
            className="onboarding-message-left"
            isVisible={showPantryIntro}
          />

          {showGoShoppingBanner && isPantryShrinkingState && !showPantryStarter ? (
            <StateBanner
              variant="soft"
              eyebrow="Pantry is light"
              body={
                <>
                  <b>Stocking up</b> changes which recipes are cookable.
                </>
              }
              action={onGoShopping ? { label: 'Plan a shop →', onClick: onGoShopping } : undefined}
              className="pantry-state-banner pantry-banner-stack"
            />
          ) : null}

          {showPantryStarterReopen ? (
            <PantryUnlockMealsBanner onAddItems={reopenPantryStarter} />
          ) : null}

          {showPantryStarter ? null : (
            <PantryAddFeedbackBanner
              lastResult={lastResult}
              addedItemNames={lastAddedNames}
              unrecognizedItems={lastResult?.not_imported}
              onDismiss={() => setLastResult(null)}
              onTryManual={() => {
                setLastResult(null)
                openAddPanel('item')
              }}
            />
          )}

          {showLowStockBanner ? (
            <PantryLowStockBanner onGoShopping={onGoShopping} onPasteReceipt={handleOpenReceiptTab} />
          ) : null}

          {error && !showPantryStarter ? <p className="pantry-inline-error">{error}</p> : null}

          {inventoryContent}

          <Modal
            open={bulkRemoveConfirm}
            onClose={() => {
              if (!editActionsBusy) setBulkRemoveConfirm(false)
            }}
            titleId="remove-items-confirm-title"
            describedById="remove-items-confirm-description"
            overlayClassName="shop-save-dialog-overlay"
            contentClassName="pantry-confirm-panel"
          >
            <h2 id="remove-items-confirm-title" className="pantry-confirm-title">
              Remove selected items?
            </h2>
            <p id="remove-items-confirm-description" className="pantry-confirm-description">
              Remove {selectedCount} selected item{selectedCount !== 1 ? 's' : ''} from your pantry?
            </p>
            <div className="pantry-confirm-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setBulkRemoveConfirm(false)}
                disabled={editActionsBusy}
                className="pantry-confirm-cancel-btn"
              >
                Cancel
              </Button>
              <button
                type="button"
                onClick={confirmBulkRemoveItems}
                disabled={editActionsBusy || selectedCount === 0}
                className="pantry-danger-btn"
              >
                {bulkRemoving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </Modal>

          {undoToast ? (
            <PantryRemoveUndoToast
              count={undoToast.count}
              label={undoToast.label}
              onUndo={handleUndoRemove}
              onDismiss={dismissUndoToast}
              isMobile={isMobile}
            />
          ) : null}

      </div>
      {footer}
    </div>
  )
}
